import classname from 'classnames';
import React from 'react';
import Tooltip from '@/componentsDS/Tooltip';
import Icon from '@/componentsDS/Icon/Icon';
import { IconSize, IconStyle } from '@/componentsDS/Icon/Icon.type';
import Button from '@/componentsDS/Button/Button';
import { ButtonType, ButtonVariant } from '@/componentsDS/Button/Button.type';
import useEllipsisCheck from '@/hooksDS/use-ellipsis-check/index';
import PropTypes from 'prop-types';


// Header cell (label + optional info tooltip + sort affordance), extracted from
// RenderCell to keep each function's cognitive complexity in bounds.
const HeaderCell = ({ cellClassName, style, header, index, sortConfig, handleSort }) => {
	const headerContent = (
		<>
			<div className="ds-table__header-cell__label__left">
				<span className="ds-table__label">{header.label}</span>
				{header.subLabel && <span className="ds-table__sub-label">{header.subLabel}</span>}
			</div>
			{header.info && (
				<div className="ds-table__header-cell__info">
					<Tooltip id={'ds-table__header__tooltip__' + index} content={header.info} placement={'bottom'} portal={true}>
						<Icon name="information-circle" size={IconSize.SMALL} style={IconStyle.SOLID} />
					</Tooltip>
				</div>
			)}
		</>
	);

	return (
		<div className={cellClassName} style={style}>
			{header.sortable ? (
				<Button
					variant={ButtonVariant.UTILITY}
					type={ButtonType.UTILITY}
					onClick={() => handleSort(header.key)}
					text={
						<div className="ds-table__header-cell__wrapper">
							{headerContent}

							<div className="ds-table__header-cell__sort-icon ds-table__header-cell__label__right">
								{header.key === sortConfig?.key ? (
									<Icon name="arrow-small-down" size={IconSize.MEDIUM} style={IconStyle.OUTLINED} />
								) : (
									<Icon name="arrows-up-down" size={IconSize.MEDIUM} style={IconStyle.OUTLINED} />
								)}
							</div>
						</div>
					}
				/>
			) : (
				<div className="ds-table__header-cell__wrapper">{headerContent}</div>
			)}
			<div className="ds-table__header-cell__right-line" />
		</div>
	);
};

HeaderCell.propTypes = {
	cellClassName: PropTypes.string,
	style: PropTypes.object,
	header: PropTypes.object,
	index: PropTypes.number,
	sortConfig: PropTypes.object,
	handleSort: PropTypes.func
};

// Plain (non-custom-render) body cell text with its ellipsis tooltip — extracted
// from RenderCell to keep each function's cognitive complexity in bounds.
const BodyCellText = ({ header, row, index, isEllipsis, elementRefs }) => (
	<Tooltip id={`ds-table__cell__tooltip__${index}`} content={row ? (row[header.key]) : ''} disabled={!isEllipsis} portal={true}>
		{row?.link && !header.actionable ? (
			<a href={row.link} ref={(el) => (elementRefs.current[index] = el)}>
				{row[header.key]}
			</a>
		) : (
			<div ref={(el) => (elementRefs.current[index] = el)}>{row ? (row[header.key]) : null}</div>
		)}
	</Tooltip>
);

BodyCellText.propTypes = {
	header: PropTypes.object,
	row: PropTypes.object,
	index: PropTypes.number,
	isEllipsis: PropTypes.bool,
	elementRefs: PropTypes.object
};

const RenderCell = ({ header, index, row, isHeader, leftFreeze, rightFreeze, headers, handleSort, sortConfig }) => {
	const isFrozenLeft = leftFreeze && index < 1;
	const isFrozenRight = rightFreeze && index >= headers.length - 1;

	const { isEllipsis, elementRefs } = useEllipsisCheck();

	const cellClassName = classname('ds-table__cell', {
		'ds-table__header-cell': isHeader,
		'ds-table__header-cell--sortable': isHeader && header.sortable,
		'ds-table__header-cell--sorted': isHeader && header.key === sortConfig?.key,
		'ds-table__header-cell--asc': isHeader && sortConfig?.direction === 'asc',
		'ds-table__cell--frozen-left': isFrozenLeft,
		'ds-table__cell--frozen-right': isFrozenRight,
		'ds-table__cell--link': row?.link && !header.actionable
	});

	const style = {
		flexBasis: header.width ?? '150px',
		minWidth: header.width ?? '150px',
		...(isFrozenLeft && { left: `56px` }),
		...(isFrozenRight && { right: `0px` }) 
	};

	const styleText = {
		flexBasis: (header?.width && header.width) || '150px'
	};

	if (isHeader) {
		return (
			<HeaderCell
				cellClassName={cellClassName}
				style={style}
				header={header}
				index={index}
				sortConfig={sortConfig}
				handleSort={handleSort}
			/>
		);
	} else {

		return (
			<div className={cellClassName} style={style}>
				<div className="ds-table__cell__text" style={styleText}>
					{header.render
						? (row ? header.render(row[header.key], row) : null)
						: <BodyCellText header={header} row={row} index={index} isEllipsis={isEllipsis} elementRefs={elementRefs} />}
				</div>
			</div>
		);
	}
};

export default RenderCell;


RenderCell.propTypes = {
	row: PropTypes.object,
	header: PropTypes.object,
	index: PropTypes.number,
	isHeader: PropTypes.bool,
	handleSort: PropTypes.func,
	sortConfig: PropTypes.object,
	rightFreeze: PropTypes.bool,
	leftFreeze: PropTypes.bool,
	headers: PropTypes.array
}